<template>
  <div class="analysis-selector">
    <Transition name="slide-fade" appear>
      <div v-if="isOpen" class="mb-0 overflow-hidden bg-white shadow-lg">
        <!-- Header da seção -->
        <div class="flex items-center justify-between bg-gray-50 px-4 py-3">
          <h3 class="text-lg font-semibold text-gray-800">Seleção de Análise</h3>
          <button @click="$emit('close')" class="p-1 text-gray-500 transition-colors hover:text-gray-700" title="Fechar completamente">
            <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>

        <!-- Conteúdo da seção -->
        <div class="px-6 py-2">
          <!-- Descrição -->
          <div class="mb-6">
            <p class="mb-4 text-gray-600">Selecione uma análise para executar neste sensor. Cada análise possui configurações específicas.</p>
          </div>

          <!-- Lista de análises disponíveis -->
          <div class="mb-6">
            <h4 class="text-md mb-4 font-semibold text-gray-800">Análises Disponíveis</h4>

            <!-- Análises para sensores de energia -->
            <div v-if="isEnergySensor" class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              <!-- Análise de Ciclo de Mistura -->
              <AnalysisCard
                :analysis="cycleAnalysisConfig"
                :selected="selectedAnalysis === 'cycle_analysis'"
                :disabled="false"
                @click="selectAnalysis('cycle_analysis')" />

              <AnalysisCard
                :analysis="correlationAnalysisConfig"
                :selected="selectedAnalysis === 'correlation_analysis'"
                :disabled="false"
                @click="selectAnalysis('correlation_analysis')" />

              <!-- Análise de Eficiência Energética -->
              <AnalysisCard
                :analysis="energyEfficiencyConfig"
                :selected="selectedAnalysis === 'energy_efficiency'"
                :disabled="true"
                @click="selectAnalysis('energy_efficiency')" />

              <!-- Análise de Padrões Temporais -->
              <AnalysisCard
                :analysis="temporalPatternsConfig"
                :selected="selectedAnalysis === 'temporal_patterns'"
                :disabled="true"
                @click="selectAnalysis('temporal_patterns')" />

              <!-- Análise de Anomalias -->
              <AnalysisCard
                :analysis="anomalyDetectionConfig"
                :selected="selectedAnalysis === 'anomaly_detection'"
                :disabled="true"
                @click="selectAnalysis('anomaly_detection')" />
            </div>

            <!-- Análises para sensores de temperatura -->
            <div v-else-if="isTemperatureSensor" class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              <AnalysisCard
                :analysis="correlationAnalysisConfig"
                :selected="selectedAnalysis === 'correlation_analysis'"
                :disabled="false"
                @click="selectAnalysis('correlation_analysis')" />

              <!-- Análise de Padrões Temporais para temperatura -->
              <AnalysisCard
                :analysis="temperaturePatternsConfig"
                :selected="selectedAnalysis === 'temperature_patterns'"
                :disabled="true"
                @click="selectAnalysis('temperature_patterns')" />

              <!-- Análise de Anomalias para temperatura -->
              <AnalysisCard
                :analysis="temperatureAnomalyConfig"
                :selected="selectedAnalysis === 'temperature_anomaly'"
                :disabled="true"
                @click="selectAnalysis('temperature_anomaly')" />
            </div>

            <!-- Análises para sensores de abertura de porta -->
            <div v-else-if="isDoorSensor" class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              <AnalysisCard
                :analysis="correlationAnalysisConfig"
                :selected="selectedAnalysis === 'correlation_analysis'"
                :disabled="false"
                @click="selectAnalysis('correlation_analysis')" />

              <!-- Análise de Padrões de Abertura -->
              <AnalysisCard
                :analysis="doorPatternsConfig"
                :selected="selectedAnalysis === 'door_patterns'"
                :disabled="true"
                @click="selectAnalysis('door_patterns')" />

              <!-- Análise de Anomalias para porta -->
              <AnalysisCard
                :analysis="doorAnomalyConfig"
                :selected="selectedAnalysis === 'door_anomaly'"
                :disabled="true"
                @click="selectAnalysis('door_anomaly')" />
            </div>

            <!-- Placeholder quando não há análises disponíveis -->
            <div v-else class="py-12 text-center">
              <div class="mx-auto mb-4 flex h-16 w-16 items-center justify-center bg-gray-100">
                <svg class="h-8 w-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"></path>
                </svg>
              </div>
              <h3 class="mb-2 text-lg font-medium text-gray-900">Nenhuma análise disponível</h3>
              <p class="mb-4 text-gray-500">Este tipo de sensor ainda não possui análises implementadas.</p>
              <p class="text-sm text-gray-400">Tipo de sensor: {{ sensorTypeName }}</p>
            </div>
          </div>
        </div>

        <!-- Footer com botões -->
        <div class="flex items-center justify-end bg-gray-2 p-4">
          <div class="flex">
            <button
              @click="$emit('close')"
              class="bg-gray-200 px-4 py-2 text-gray-700 transition-colors hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500">
              Cancelar
            </button>

            <button
              @click="proceedToAnalysis"
              :disabled="!selectedAnalysis"
              class="ml-4 bg-blue px-6 py-2 font-medium text-white transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50">
              <span class="flex items-center">
                <svg class="mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6"></path>
                </svg>
                Próximo
              </span>
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import AnalysisCard from '@/components/Registers/AnalysisCard.vue'
import { SensorType } from '@/interfaces/enums'
import { SignalIcon } from '@heroicons/vue/24/outline'
import { computed, ref, watch } from 'vue'

// Props e Emits
interface Props {
  isOpen: boolean
  sensorData?: any
}

interface Emits {
  (e: 'close'): void
  (e: 'proceed-to-analysis', analysis: string): void
}

const props = withDefaults(defineProps<Props>(), {
  isOpen: false,
  sensorData: null,
})

const emit = defineEmits<Emits>()

// Estado reativo
const selectedAnalysis = ref<string>('')

// Computed para verificar tipo de sensor
const isEnergySensor = computed(() => {
  return props.sensorData?.sensor?.sensor_type === SensorType.ELECTRICITY
})

const isTemperatureSensor = computed(() => {
  return props.sensorData?.sensor?.sensor_type === SensorType.TEMPERATURE
})

const isDoorSensor = computed(() => {
  return props.sensorData?.sensor?.sensor_type === SensorType.DOOR_OPEN
})

const sensorTypeName = computed(() => {
  const sensorType = props.sensorData?.sensor?.sensor_type
  const names = {
    [SensorType.ELECTRICITY]: 'Energia',
    [SensorType.TEMPERATURE]: 'Temperatura',
    [SensorType.DOOR_OPEN]: 'Abertura de Porta',
  }
  return names[sensorType] || 'Desconhecido'
})

const correlationAnalysisConfig = {
  id: 'correlation_analysis',
  title: 'Análise de Correlação',
  description: 'Correlação entre sensores',
  longDescription: 'Identifica padrões de correlação entre diferentes variáveis',
  iconComponent: SignalIcon,
  color: 'blue',
}

// Configurações das análises para energia
const cycleAnalysisConfig = {
  id: 'cycle_analysis',
  title: 'Análise de Ciclo',
  description: 'Detecção de ciclos de mistura',
  longDescription: 'Identifica e analisa ciclos de mistura baseados em padrões de consumo de energia',
  icon: 'M13 10V3L4 14h7v7l9-11h-7z',
  color: 'blue',
}

const energyEfficiencyConfig = {
  id: 'energy_efficiency',
  title: 'Eficiência Energética',
  description: 'Análise de consumo otimizado',
  longDescription: 'Avalia a eficiência energética e identifica oportunidades de otimização',
  icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
  color: 'green',
}

const temporalPatternsConfig = {
  id: 'temporal_patterns',
  title: 'Padrões Temporais',
  description: 'Análise de tendências temporais',
  longDescription: 'Identifica padrões e tendências temporais no consumo de energia',
  icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
  color: 'purple',
}

const anomalyDetectionConfig = {
  id: 'anomaly_detection',
  title: 'Detecção de Anomalias',
  description: 'Identificação de comportamentos anômalos',
  longDescription: 'Detecta comportamentos anômalos e desvios nos padrões de consumo',
  icon: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z',
  color: 'orange',
}

// Configurações das análises para temperatura
const temperaturePatternsConfig = {
  id: 'temperature_patterns',
  title: 'Padrões de Temperatura',
  description: 'Análise de variações térmicas',
  longDescription: 'Identifica padrões e variações na temperatura ao longo do tempo',
  icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
  color: 'red',
}

const temperatureAnomalyConfig = {
  id: 'temperature_anomaly',
  title: 'Anomalias Térmicas',
  description: 'Detecção de variações anômalas',
  longDescription: 'Detecta variações anômalas na temperatura que podem indicar problemas',
  icon: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z',
  color: 'orange',
}

// Configurações das análises para porta
const doorPatternsConfig = {
  id: 'door_patterns',
  title: 'Padrões de Abertura',
  description: 'Análise de frequência de uso',
  longDescription: 'Identifica padrões de abertura e fechamento da porta',
  icon: 'M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z',
  color: 'indigo',
}

const doorAnomalyConfig = {
  id: 'door_anomaly',
  title: 'Anomalias de Abertura',
  description: 'Detecção de aberturas anômalas',
  longDescription: 'Detecta aberturas anômalas que podem indicar problemas de segurança',
  icon: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z',
  color: 'red',
}

// Funções
const selectAnalysis = (analysisType: string) => {
  selectedAnalysis.value = analysisType
}

const getAnalysisName = (analysisType: string): string => {
  const names: Record<string, string> = {
    cycle_analysis: 'Análise de Ciclo',
    correlation_analysis: 'Análise de Correlação',
    energy_efficiency: 'Eficiência Energética',
    temporal_patterns: 'Padrões Temporais',
    anomaly_detection: 'Detecção de Anomalias',
    temperature_patterns: 'Padrões de Temperatura',
    temperature_anomaly: 'Anomalias Térmicas',
    door_patterns: 'Padrões de Abertura',
    door_anomaly: 'Anomalias de Abertura',
  }
  return names[analysisType] || analysisType
}

const proceedToAnalysis = () => {
  if (!selectedAnalysis.value) return

  // Emitir evento com a análise selecionada
  emit('proceed-to-analysis', selectedAnalysis.value)
}

// Watcher para resetar seleção quando abrir
watch(
  () => props.isOpen,
  (newValue) => {
    if (newValue) {
      selectedAnalysis.value = ''
    }
  },
)
</script>

<style scoped>
/* Transições suaves */
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.3s ease;
}

.slide-fade-enter-from {
  opacity: 0;
  transform: translateY(-20px) scale(0.95);
}

.slide-fade-leave-to {
  opacity: 0;
  transform: translateY(-20px) scale(0.95);
}

/* Hover effects */
.cursor-pointer:hover {
  transform: translateY(-2px);
}

/* Checkbox animation */
.w-5.h-5 {
  transition: all 0.2s ease;
}
</style>
